import { useQuery } from "@tanstack/react-query";
import type { createAgentClient } from "../api/agentClient";
import type { BoardQueueRow } from "../api/clinicalWorkspace";
import { boardRoom } from "../lib/todayBoard";

type TodayPatientSnapshotClient = Partial<
  Pick<ReturnType<typeof createAgentClient>, "getPatientWorkspace">
>;

export function TodayPatientSnapshot({
  siteId,
  row,
  client,
}: {
  readonly siteId: string;
  readonly row: BoardQueueRow;
  readonly client: TodayPatientSnapshotClient;
}) {
  const {
    data: workspace,
    isPending,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["today-patient-snapshot", siteId, row.id, row.encounterId],
    enabled: client.getPatientWorkspace !== undefined,
    queryFn: async ({ signal }) => {
      if (!client.getPatientWorkspace) throw new Error("Chart snapshot is unavailable.");
      const result = await client.getPatientWorkspace(row.id, row.encounterId, signal);
      if (
        result.id !== row.id ||
        result.encounterId !== row.encounterId ||
        result.siteId !== siteId
      ) {
        throw new Error("The chart snapshot does not match the selected visit.");
      }
      return result;
    },
    // Switching selection must never retain another patient's clinical details.
    placeholderData: () => undefined,
    staleTime: 10_000,
    retry: false,
  });

  return (
    <section className="tb-snapshot" aria-label="Patient snapshot">
      <h3 className="sr-only">Snapshot</h3>
      {!client.getPatientWorkspace ? (
        <p className="tb-muted">Chart snapshot is unavailable.</p>
      ) : isPending ? (
        <p className="tb-muted" role="status">
          Loading chart snapshot…
        </p>
      ) : isError ? (
        <div>
          <p className="tb-muted" role="alert">
            The chart snapshot could not be loaded.
          </p>
          <button
            type="button"
            className="tb-button"
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            {isFetching ? "Retrying…" : "Retry snapshot"}
          </button>
        </div>
      ) : workspace ? (
        <dl className="tb-clinical-summary">
          <div>
            <dt>Vitals</dt>
            <dd>
              {workspace.vitals.length
                ? workspace.vitals
                    .map(
                      (vital) =>
                        `${vital.label}: ${vital.value}${vital.flag === "high" ? " (high)" : ""}`,
                    )
                    .join(" · ")
                : "Not recorded"}
            </dd>
          </div>
          <div>
            <dt>Allergies</dt>
            <dd>{workspace.allergies.length ? workspace.allergies.join(" · ") : "Not recorded"}</dd>
          </div>
          <div>
            <dt>Medications</dt>
            <dd>
              {workspace.medications.length ? workspace.medications.join(" · ") : "Not recorded"}
            </dd>
          </div>
          <div>
            <dt>History</dt>
            <dd>{workspace.problems.length ? workspace.problems.join(" · ") : "Not recorded"}</dd>
          </div>
        </dl>
      ) : null}
      <dl className="tb-visit-summary">
        <div>
          <dt>Clinician</dt>
          <dd>{row.clinician ?? "Unassigned"}</dd>
        </div>
        <div>
          <dt>Room</dt>
          <dd>{boardRoom(row) ?? "Lobby"}</dd>
        </div>
        <div>
          <dt>Recorded urgency</dt>
          <dd className={row.urgency ? `tb-urgency-${row.urgency}` : undefined}>
            {row.urgency ?? "Not recorded"}
          </dd>
        </div>
        <div>
          <dt>Coverage</dt>
          <dd>{row.coverage ?? "Not recorded"}</dd>
        </div>
        <div>
          <dt>Open tasks</dt>
          <dd>{row.tasks ?? "Not available"}</dd>
        </div>
      </dl>
    </section>
  );
}
